import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { UploadQueue } from "../components/UploadQueue";
import { ManageList } from "../components/ManageList";
import { AdsManager } from "../components/AdsManager";
import { SiteCodeEditor } from "../components/SiteCodeEditor";
import { ScheduleCalendar } from "../components/ScheduleCalendar";
import { CommentsModeration } from "../components/CommentsModeration";
import { LogoMark } from "../components/Logo";
import { http, asList } from "../lib/api";

export default function Admin() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(location.state?.user || null);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ videos: 0, photos: 0, views: 0 });
  const [pending, setPending] = useState(0);

  useEffect(() => {
    if (user) return;
    http.get("/auth/me").then((r) => setUser(r.data)).catch(() => navigate("/admin/login", { replace: true }));
  }, [user, navigate]);

  const load = useCallback(() => {
    http.get("/media", { params: { limit: 200, pinned_first: true, scheduled: true } }).then((r) => setItems(asList(r))).catch(() => {});
    http.get("/admin/stats").then((r) => setStats(r.data ?? stats)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const logout = async () => {
    await http.post("/auth/logout").catch(() => {});
    navigate("/", { replace: true });
  };

  if (!user) return <div className="flex min-h-screen items-center justify-center"><div className="animate-pulse"><LogoMark size={48} /></div></div>;

  const tab = "px-5 py-3 text-xs uppercase tracking-[0.18em] text-slate-400 data-[state=active]:bg-rose-600 data-[state=active]:text-white rounded-none";

  return (
    <div className="mx-auto min-h-screen max-w-[1200px] px-5 pt-32 sm:px-10" data-testid="admin-dashboard">
      <div className="mb-12 flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="eyebrow mb-3">Painel · {user.email}</p>
          <h1 className="font-serif-d text-4xl font-semibold tracking-tight text-white sm:text-6xl">Olá, <span className="italic text-rose-500">{user.name?.split(" ")[0]}</span></h1>
        </div>
        <button onClick={logout} className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-300 hover:text-rose-400" data-testid="admin-logout-button"><LogOut size={14} /> Sair</button>
      </div>

      <div className="mb-12 grid grid-cols-3 border border-white/5" data-testid="admin-stats">
        {[["Vídeos", stats.videos], ["Fotos", stats.photos], ["Visualizações", stats.views]].map(([l, v], i) => (
          <div key={l} className={`p-5 sm:p-8 ${i ? "border-l border-white/5" : ""}`}>
            <p className="font-serif-d text-3xl text-white sm:text-5xl" data-testid={`stat-${l.toLowerCase()}`}>{v ?? 0}</p>
            <p className="eyebrow mt-2 !text-[10px] !tracking-[0.12em] !text-slate-400 sm:!text-xs sm:!tracking-[0.28em]">{l}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="upload">
        <TabsList className="mb-10 h-auto flex-wrap justify-start rounded-none bg-[#0d0c11] p-1">
          <TabsTrigger value="upload" className={tab} data-testid="tab-upload">Enviar</TabsTrigger>
          <TabsTrigger value="manage" className={tab} data-testid="tab-manage">Gerenciar ({items.length})</TabsTrigger>
          <TabsTrigger value="agenda" className={tab} data-testid="tab-agenda">Agenda</TabsTrigger>
          <TabsTrigger value="comments" className={tab} data-testid="tab-comments">Comentários{pending > 0 && <span className="ml-2 rounded-full bg-amber-400 px-1.5 text-[10px] text-black" data-testid="pending-comments-badge">{pending}</span>}</TabsTrigger>
          <TabsTrigger value="ads" className={tab} data-testid="tab-ads">Propagandas</TabsTrigger>
        </TabsList>
        <TabsContent value="upload" forceMount className="data-[state=inactive]:hidden"><UploadQueue onDone={load} /></TabsContent>
        <TabsContent value="manage"><ManageList items={items} onChange={load} /></TabsContent>
        <TabsContent value="agenda"><ScheduleCalendar items={items} onChange={load} /></TabsContent>
        <TabsContent value="comments" forceMount className="data-[state=inactive]:hidden"><CommentsModeration onPending={setPending} /></TabsContent>
        <TabsContent value="ads"><AdsManager /><SiteCodeEditor /></TabsContent>
      </Tabs>
    </div>
  );
}
